import {useBleskAuth, useTeamSubscription} from '@blesk-frontend/auth';
import {Button, Popper, useModal} from '@blesk-frontend/ui';
import {sendToBackgroundViaRelay} from '@plasmohq/messaging';
import {useNavigate} from '@tanstack/react-router';
import {useCallback} from 'react';
import {toast} from 'sonner';

import {GuideCreateMenu} from '@/features/guides';
import {PlanPaywallModal} from '@/features/plans';
import {useAppPlatform, useUserAgent} from '@/hooks';
import {sendEvent} from '@/libs/analytics';

interface GuideCreateButtonProps {
  menuPlacement?: 'bottom-start' | 'bottom-end';
}

const openSidePanel = async () => {
  return sendToBackgroundViaRelay({
    // @ts-expect-error
    name: 'open-side-panel',
  });
};

export const GuideCreateButton = ({menuPlacement = 'bottom-end'}: GuideCreateButtonProps) => {
  const navigate = useNavigate();
  const {token} = useBleskAuth();
  const {extensionStatus, windowsStatus} = useAppPlatform();
  const {isDesktop} = useUserAgent();
  const {isAllowCreateGuide} = useTeamSubscription();

  const openPaywallModal = useModal((props) => <PlanPaywallModal {...props} />);

  const isExtensionInstalled = extensionStatus === 'installed';
  const isWindowsInstalled = windowsStatus === 'installed';

  const onClickCreateGuide = useCallback(
    (type: 'manual' | 'extension' | 'windows') => {
      if (!isAllowCreateGuide) {
        openPaywallModal();
        return;
      }

      switch (type) {
        case 'manual': {
          sendEvent('guides/create:try-manual');
          navigate({to: '/guides/create'});
          break;
        }
        case 'extension': {
          if (!isExtensionInstalled) {
            navigate({to: '/agents/extension'});
            return;
          }
          try {
            sendEvent('guides/create:try-extension');
            openSidePanel();
          } catch (_err) {
            toast.error(
              'Не удалось открыть панель расширения, обновите страницу и попробуйте еще раз',
            );
          }
          break;
        }
        case 'windows': {
          if (!isWindowsInstalled) {
            navigate({to: '/agents/windows'});
            return;
          }
          sendEvent('guides/create:try-windows');
          location.href = `blesk://auth?token=${token}`;
          break;
        }
      }
    },
    [
      navigate,
      openPaywallModal,
      isExtensionInstalled,
      isAllowCreateGuide,
      token,
      isWindowsInstalled,
    ],
  );

  return (
    <Popper
      menu={
        <GuideCreateMenu
          onCreateManual={() => onClickCreateGuide('manual')}
          onRecordExtension={() => onClickCreateGuide('extension')}
          onRecordWindows={() => onClickCreateGuide('windows')}
        />
      }
      placement={menuPlacement}
    >
      <Button
        className="w-40"
        data-attr="guide/create-button"
        loading={extensionStatus === 'loading'}
        disabled={!isDesktop}
      >
        Новая инструкция
      </Button>
    </Popper>
  );
};
